import { Component, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ReportsService } from '../../reports.service';
import { ToastrService } from 'ngx-toastr';
import { ToastrUtilsService, TOASTR_CONFIG } from '../../../shared/utils/toastr-utils';
import { BodyScrollEvent, ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { ExportDataService } from '../../../shared/services/export-data.service';
import { DatePipe } from '@angular/common';
import { InbuiltUtilityService } from '../inbuilt-utility.service';
import { Subject, take, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SharedAuthService } from '../../../shared/shared.auth.service';;



@Component({
  selector: 'app-inbuilt-table-v2',
  standalone: false,
  templateUrl: './inbuilt-table-v2.component.html',
  styleUrl: './inbuilt-table-v2.component.css'
})



export class InbuiltTableV2Component implements OnInit, OnDestroy {
  @Input() config: any;
  @Input() elementData: any = null;


  defaultColDef: ColDef = {
    sortable: true,
    filter: false,
    resizable: true,
    flex: 1,
    minWidth: 150,
  };
  tableDataTotalCount = 0;
  columnDefs: ColDef[] = [];
  columns: string[] = [];
  reportNameFromQueryParams = '';
  tableData: any[] = [];
  filteredTableData: any[] = [];
  showTable = false;
  loading = false;
  tableLoader = false;
  pageNumber = 0;
  currentDate: Date | null = null;
  currentTime = '';
  selectedFilters: any = {};
  isNoneValueExists = false;
  isExporting = false;
  isDownloadDisabled = false;
  isAllDataFetched = false;
  limit = 5000;
  maxResultCount = 0;
  attributeList: string[] = [];
  selectedAttributes: string[] = [];
  attributeDropdownData: Array<{ name: string; value: string }> = [];
  attributeInitialSelection = 'All';
  attributeResetCounter = 0;

  private readonly directDownloadLimit = 5000;
  private readonly scrollTriggerBuffer = 2000;
  private readonly refreshCancel$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private gridApi!: GridApi;
  private sortObject: { colId: string; sort: string } | null = null;

  constructor(
    private reportsService: ReportsService,
    private toastr: ToastrService,
    private toastrUtilsService: ToastrUtilsService,
    private exportDataService: ExportDataService,
    private datePipe: DatePipe,
    private inbuiltUtilityService: InbuiltUtilityService,
    private sharedAuthService: SharedAuthService,
    private renderer: Renderer2
  ) { }

  ngOnInit(): void {
    this.reportNameFromQueryParams = this.config?.reportNameFromQueryParams || '';
    this.showTable = true;

    this.applyLatestFilterSnapshotForCurrentScope();

    this.inbuiltUtilityService.getSelectedFilterEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (this.shouldApplyFilterEvent(event)) {
          this.applySelectedFilterEvent(event);
        }
      });

    this.updateGmtTimestamp();
    if (!this.showTable || this.isNoneValueExists) {
      this.clearRenderedTable();
      return;
    }

    this.fetchTableData(true);
  }

  ngOnDestroy(): void {
    this.refreshCancel$.next();
    this.refreshCancel$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  onBodyScroll(_: BodyScrollEvent): void {
    if (!this.gridApi || this.loading || this.isAllDataFetched) {
      return;
    }

    const lastVisibleRow = this.gridApi.getLastDisplayedRowIndex();
    const totalRows = this.gridApi.getDisplayedRowCount();
    const triggerIndex = Math.max(totalRows - this.scrollTriggerBuffer, 0);

    if (lastVisibleRow >= triggerIndex) {
      this.fetchTableData(false);
    }
  }

  onSortChanged(event: any): void {
    if (this.isAllDataFetched) {
      return;
    }

    const sortState = event.api
      .getColumnState()
      .filter((column: any) => !!column.sort)
      .map((column: any) => ({ colId: column.colId, sort: column.sort }));

    const nextSort = sortState.length > 0 ? sortState[0] : null;
    if (JSON.stringify(this.sortObject) === JSON.stringify(nextSort) && this.tableData.length > 0) {
      return;
    }

    this.sortObject = nextSort;
    this.resetTableState({ cancelRunningRequests: true, clearSort: false });
    this.fetchTableData(false);
  }

  onAttributeSelectionChange(event: any[]): void {
    if (!Array.isArray(event) || this.attributeList.length === 0) {
      return;
    }

    const allSentinel = event.find((item: any) => item?.__selectAll__ === true);
    let selected: string[] = [];

    if (allSentinel) {
      const excluded = new Set<string>(
        (Array.isArray(allSentinel.__excludeNames) ? allSentinel.__excludeNames : [])
          .map((name: any) => String(name ?? '').trim())
          .filter(Boolean)
      );
      // attributeList preserves the dropdown order so filtering it directly keeps columns
      // in the same order as the dropdown.
      selected = this.attributeList.filter((name: string) => !excluded.has(name));
    } else {
      // Build the set of chosen names first, then re-order by attributeList position so
      // the rendered columns always appear in the same order as the dropdown list.
      const chosenSet = new Set<string>(
        event
          .map((item: any) => String(item?.name ?? item?.value ?? item ?? '').trim())
          .filter((name: string) => !!name)
      );
      selected = this.attributeList.filter((name: string) => chosenSet.has(name));
    }

    if (selected.length === 0) {
      return;
    }

    this.selectedAttributes = selected;
    this.applyColumnDefs(selected);
  }

  refreshTableData(): void {
    this.updateGmtTimestamp();
    this.resetTableState({ cancelRunningRequests: true, clearSort: true });
    this.fetchTableData(false);
  }

  downloadAndExportToExcel(): void {
    this.isExporting = true;
    this.isDownloadDisabled = true;

    if (!this.maxResultCount) { 
      this.toastrUtilsService.warning('No data available to export', '', {
          enableHtml: true
        });
      this.stopExportLoader();
      return;
    }

    if (this.shouldUseAdfDownload()) {
      if (this.maxResultCount >= TOASTR_CONFIG.ROW_LIMIT) {
        this.toastrUtilsService.warning('The requested extract exceeds the maximum limit of 500,000 rows.<br>Please refine your filters and run the extract again.', '', {
          enableHtml: true
        });
        this.stopExportLoader();
        return;
      }
      this.callADFforDownload();
      return;
    }

    if (this.isAllDataFetched) {
      this.exportCurrentRowsToExcel(this.tableData);
      this.stopExportLoader();
      return;
    }

    // Data is not fully loaded yet but ADF is not needed.
    // Pass the next page number with download=true so the API returns all remaining
    // records in one shot. We then prepend the rows already in memory so the export
    // contains the complete dataset without making one request per page.
    const nextPage = this.pageNumber + 1;
    const payload = this.buildTablePayload(nextPage, true);

    this.toastr.success('Please wait, we are fetching your data and preparing the download', '', {
      timeOut: 5000,
      positionClass: 'toast-top-center'
    });

    this.reportsService.getInbuildTableData(payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const remainingRows = Array.isArray(response?.data) ? response.data : [];
          // Combine already-fetched rows with the remaining rows returned by the API.
          const allRows = [...this.tableData, ...remainingRows];
          this.exportCurrentRowsToExcel(allRows);
          this.stopExportLoader();
        },
        error: (error) => {
          this.stopExportLoader();
          this.handleErrors(error);
        }
      });
  }

  callADFforDownload(): void {
    const msid = this.sharedAuthService.getMsid() || '';
    const username = this.sharedAuthService.getUserName() || '';
    const payload = {
      report: this.config?.reportNameFromQueryParams || '',
      selectedFilters: this.inbuiltUtilityService.getTablePayload(this.selectedFilters),
      msid,
      username,
      filename: `${this.config?.reportNameFromQueryParams || ''}`,
      page_name: this.config?.currentPage ? this.config.currentPage.key : '',
      filter: this.elementData?.conditions || {},
      defaultQuery: this.elementData?.defaultQuery || {},
      baseWhere: this.elementData?.baseWhere || {},
      report_type: 'inBuilt',
      tab_name: this.config?.currentTab ? this.config.currentTab.key : '',
      element_type: this.elementData ? this.elementData.type : '',
      element_name: this.elementData ? this.elementData.name : ''
    };
    this.callADFPipelineAPI(payload);
  }

  callADFPipelineAPI(payload: any): void {
    this.reportsService.callADFPipelineForExcelExport(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.message === 'success') {
            this.toastr.success('Your export has been initiated. It will be available in <a href="/my-downloads" target="_self">My Downloads</a> in a few minutes.', '', {
              enableHtml: true,
              timeOut: 5000,
              positionClass: 'toast-top-center'
            });
          } else {
            this.toastr.error('There was an error initiating your export. Please try again.', '', {
              timeOut: 5000,
              positionClass: 'toast-top-center'
            });
          }
          this.stopExportLoader();
        },
        error: () => {
          this.toastr.error('There was an error initiating your export. Please try again.', '', {
            timeOut: 5000,
            positionClass: 'toast-top-center'
          });
          this.stopExportLoader();
        }
      });
  }

  private resolveFilterKeys(filterRef: any): { displayKey: string; queryKey: string } {
    if (filterRef && typeof filterRef === 'object' && !Array.isArray(filterRef)) {
      const entries = Object.entries(filterRef);
      if (entries.length > 0) {
        const [displayKey, queryKey] = entries[0];
        return {
          displayKey: String(displayKey ?? ''),
          queryKey: String(queryKey ?? displayKey ?? '')
        };
      }
    }

    const key = String(filterRef ?? '');
    return { displayKey: key, queryKey: key };
  }

  private shouldApplyFilterEvent(event: any): boolean {
    if (!event || !this.elementData || !this.config) {
      return false;
    }

    const eventReport = String(event?.reportNameFromQueryParams ?? '');
    const currentReport = String(this.config?.reportNameFromQueryParams ?? '');
    const eventPage = String(event?.currentPage?.key ?? event?.page ?? '');
    const currentPage = String(this.config?.currentPage?.key ?? '');
    const eventTab = String(event?.currentTab?.key ?? event?.tab ?? '');
    const currentTab = String(this.config?.currentTab?.key ?? '');

    if (eventReport !== currentReport || eventPage !== currentPage || eventTab !== currentTab) {
      return false;
    }

    if (typeof event?.showTable === 'boolean') {
      return true;
    }

    const filterName = String(event?.filterName ?? '');
    if (!filterName) {
      return false;
    }

    const rawDependencies = this.elementData?.filterList;
    if (!rawDependencies) {
      return true;
    }

    let dependencies: any[] = [];
    if (Array.isArray(rawDependencies)) {
      dependencies = rawDependencies;
    } else if (typeof rawDependencies === 'string') {
      try {
        const parsed = JSON.parse(rawDependencies);
        dependencies = Array.isArray(parsed) ? parsed : [rawDependencies];
      } catch {
        dependencies = [rawDependencies];
      }
    }

    if (dependencies.length === 0) {
      return true;
    }

    return dependencies.some((dependency: any) => {
      const dependencyKeys = this.resolveFilterKeys(dependency);
      return dependencyKeys.displayKey === filterName
        || dependencyKeys.queryKey === filterName
        || String(dependency ?? '') === filterName;
    });
  }

  private applySelectedFilterEvent(event: any): void {
    this.syncStateFromFilterEvent(event);

    this.refreshCancel$.next();

    if (!this.showTable || this.isNoneValueExists) {
      this.clearRenderedTable();
      return;
    }

    this.resetTableState({ cancelRunningRequests: false, clearSort: false });
    this.fetchTableData(false);
  }

  private applyLatestFilterSnapshotForCurrentScope(): void {
    const report = String(this.config?.reportNameFromQueryParams ?? '');
    const page = String(this.config?.currentPage?.key ?? '');
    const tab = String(this.config?.currentTab?.key ?? '');
    const latestEvent = this.inbuiltUtilityService.getLatestSelectedFilterEvent(report, page, tab);

    if (!latestEvent || !this.shouldApplyFilterEvent(latestEvent)) {
      return;
    }

    this.syncStateFromFilterEvent(latestEvent);
  }

  private syncStateFromFilterEvent(event: any): void {
    this.showTable = !!event?.showTable;
    this.isNoneValueExists = !!event?.isNoneValueExists;
    this.selectedFilters = event?.selectedFilters || {};
  }

  private fetchTableData(useTableLoader: boolean): void {
    if (!this.showTable || this.isNoneValueExists || this.loading || this.isAllDataFetched) {
      return;
    }

    const nextPageNumber = this.pageNumber + 1;
    const payload = this.buildTablePayload(nextPageNumber, false);

    if (useTableLoader) {
      this.tableLoader = true;
      this.loading = false;
    } else {
      this.loading = true;
    }

    this.reportsService.getInbuildTableData(payload)
      .pipe(takeUntil(this.refreshCancel$), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const rows = Array.isArray(response?.data) ? response.data : [];

          this.pageNumber = nextPageNumber;
          this.maxResultCount = Number(response?.totalCount || 0);
          this.tableDataTotalCount = this.maxResultCount;

          if (rows.length === 0 && this.pageNumber === 1) {
            this.tableData = [];
            this.filteredTableData = [];
            this.isAllDataFetched = true;
            this.finishTableLoad();
            return;
          }

          // isFirstPage tells us whether this response is for the very first page of a fresh
          // load (filter change / refresh / sort reset). nextPageNumber was captured before
          // the request so it is safe to use here.
          const isFirstPage = nextPageNumber === 1;

          this.tableData = [...this.tableData, ...rows];

          if (this.tableData.length > 0) {
            this.isAllDataFetched = this.tableData.length >= this.maxResultCount || rows.length < this.limit;
            if (isFirstPage) {
              // First page: replace rowData entirely so ag-grid starts fresh.
              // Scroll position naturally begins at the top on a fresh load.
              this.filteredTableData = [...this.tableData];
              this.initializeAttributesFromFirstRow(this.tableData[0]);
              this.applyColumnDefs(this.getColumnsToRender());
            } else {
              // Subsequent scroll pages: append rows directly to the grid via a
              // transaction so ag-grid does NOT reset the scroll position.
              this.gridApi?.applyTransaction?.({ add: rows });

              // If all data is now loaded, enable column filters (isAllDataFetched is now
              // true) by refreshing the column definitions.
              if (this.isAllDataFetched) {
                this.applyColumnDefs(this.getColumnsToRender());
              }
            }
          } else {
            this.isAllDataFetched = true;
          }

          this.finishTableLoad();
        },
        error: (error) => {
          this.finishTableLoad();
          this.handleErrors(error);
        }
      });
  }

  private buildTablePayload(pageNumber: number, download: boolean): any {
    const sortValue = this.getApiSortValue();
    const payload: any = {
      report: this.config?.reportNameFromQueryParams || '',
      page: this.config?.currentPage ? this.config.currentPage.key : '',
      tab: this.config?.currentTab ? this.config.currentTab.key : '',
      elementName: this.elementData?.name || '',
      elementType: this.elementData?.type || '',
      limit: this.limit,
      pageNumber,
      download,
      selectedFilters: this.inbuiltUtilityService.getTablePayload(this.selectedFilters),
    };
    if (sortValue) {
      payload.sort = sortValue;
    }
    return payload;
  }

  private getApiSortValue(): string {
    if (!this.sortObject?.colId || !this.sortObject?.sort) {
      return '';
    }

    return `${this.sortObject.colId} ${this.sortObject.sort}`;
  }

  private resetTableState(options: { cancelRunningRequests: boolean; clearSort: boolean }): void {
    if (options.cancelRunningRequests) {
      this.refreshCancel$.next();
    }

    if (options.clearSort) {
      this.sortObject = null;
    }

    this.tableData = [];
    this.filteredTableData = [];
    this.tableDataTotalCount = 0;
    this.maxResultCount = 0;
    this.pageNumber = 0;
    this.isAllDataFetched = false;
    this.loading = false;
    this.tableLoader = false;
  }

  private clearRenderedTable(): void {
    this.tableData = [];
    this.filteredTableData = [];
    this.tableDataTotalCount = 0;
    this.maxResultCount = 0;
    this.pageNumber = 0;
    this.isAllDataFetched = false;
    this.loading = false;
    this.tableLoader = false;
  }

  private finishTableLoad(): void {
    this.loading = false;
    this.tableLoader = false;
  }

  private initializeAttributesFromFirstRow(firstRow: any): void {
    if (!this.shouldShowAttributeDropdown() || !firstRow) {
      return;
    }

    this.attributeList = Object.keys(firstRow || {});
    this.attributeDropdownData = this.attributeList.map((name: string) => ({ name, value: name }));

    const existingSelection = this.selectedAttributes.filter((attribute: string) => this.attributeList.includes(attribute));
    const defaultSelection = this.getDefaultAttributeSelection().filter((attribute: string) => this.attributeList.includes(attribute));
    const nextSelection = existingSelection.length > 0
      ? existingSelection
      : (defaultSelection.length > 0 ? defaultSelection : [...this.attributeList]);

    this.selectedAttributes = nextSelection;
    this.attributeInitialSelection = nextSelection.length === this.attributeList.length
      ? 'All'
      : nextSelection.join(',');
    this.attributeResetCounter += 1;
  }

  private getColumnsToRender(): string[] {
    if (this.shouldShowAttributeDropdown() && this.selectedAttributes.length > 0) {
      return this.selectedAttributes;
    }

    const configuredColumns = Array.isArray(this.config?.columns)
      ? this.config.columns.filter((column: any) => !!String(column || '').trim())
      : [];

    if (configuredColumns.length > 0) {
      return configuredColumns;
    }

    if (this.tableData.length > 0) {
      return Object.keys(this.tableData[0] || {});
    }

    return [];
  }

  private getDefaultAttributeSelection(): string[] {
    const defaultSelection = this.elementData?.defaultAttributeSelection;
    if (Array.isArray(defaultSelection) && defaultSelection.length > 0) {
      return defaultSelection;
    }

    const legacyDefaultSelection = this.elementData?.default_selection;
    if (Array.isArray(legacyDefaultSelection) && legacyDefaultSelection.length > 0) {
      return legacyDefaultSelection;
    }

    return [];
  }

  private shouldShowAttributeDropdown(): boolean {
    return !!this.elementData?.isAttributeVisibile;
  }

  private applyColumnDefs(columns: string[]): void {
    const safeColumns = Array.isArray(columns)
      ? columns.filter((column: any) => !!String(column || '').trim())
      : [];

    const valuesLinkConfig = this.elementData?.valuesLink && typeof this.elementData.valuesLink === 'object'
      ? this.elementData.valuesLink
      : {};

    this.columns = [...safeColumns];
    this.defaultColDef = {
      ...this.defaultColDef,
      filter: this.isAllDataFetched,
    };
    this.columnDefs = safeColumns.map((column: string) => ({
      field: column,
      headerName: column,
      sortable: true,
      filter: this.isAllDataFetched,
      resizable: true,
      minWidth: 150,
      flex: 1,
      cellClass: this.getColumnLinkConfig(column, valuesLinkConfig) ? 'inbuilt-table-link-cell' : '',
      cellRenderer: this.getColumnLinkConfig(column, valuesLinkConfig)
        ? (params: any) => this.renderLinkedCell(params, this.getColumnLinkConfig(column, valuesLinkConfig))
        : undefined,
      onCellClicked: this.getColumnLinkConfig(column, valuesLinkConfig)
        ? (params: any) => this.openLinkInNewTab(params)
        : undefined,
    }));

    this.gridApi?.setGridOption?.('columnDefs', this.columnDefs as any);
    this.gridApi?.refreshHeader?.();
    this.gridApi?.refreshCells?.({ force: true });
  }

  private updateGmtTimestamp(): void {
    const now = new Date();
    this.currentDate = now;
    this.currentTime = `${this.datePipe.transform(now, 'hh:mm a', 'GMT') || ''} GMT`;
  }

  private shouldUseAdfDownload(): boolean {
    return this.maxResultCount > this.directDownloadLimit && !this.isAllDataFetched;
  }

  private exportCurrentRowsToExcel(rows: any[]): void {
    const selectedFields = this.columnDefs
      .map((column: ColDef) => String(column.field || ''))
      .filter((field: string) => !!field);

    const exportRows = (rows || []).map((row: any) => {
      if (selectedFields.length === 0) {
        return row;
      }

      return selectedFields.reduce((result: Record<string, any>, field: string) => {
        result[field] = row?.[field];
        return result;
      }, {});
    });

    this.exportDataService.exportToExcel(
      this.reportNameFromQueryParams || this.config?.reportNameFromQueryParams || 'Report',
      [exportRows]
    );
  }

  private stopExportLoader(): void {
    this.isExporting = false;
    this.isDownloadDisabled = false;
  }

  handleErrors(error: any): void {
    console.error('Error: ', error);
    this.toastr.error('An error occurred while fetching data. Please try again later.', 'Error', {
      timeOut: 5000,
      positionClass: 'toast-top-center'
    });
  }

  openLinkInNewTab(params: any): void {
    const columnKey = String(params?.colDef?.field ?? params?.column?.getColDef?.()?.field ?? '').trim();
    const valuesLinkConfig = this.elementData?.valuesLink && typeof this.elementData.valuesLink === 'object'
      ? this.elementData.valuesLink
      : {};
    const linkConfig = this.getColumnLinkConfig(columnKey, valuesLinkConfig);
    if (!linkConfig) {
      return;
    }

    const resolvedUrl = this.buildLinkedUrl(linkConfig, params?.data ?? {}, columnKey, params?.value);
    if (!resolvedUrl) {
      return;
    }

    window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
  }

  private getColumnLinkConfig(columnKey: string, valuesLinkConfig: any = this.elementData?.valuesLink): any | null {
    if (!columnKey || !valuesLinkConfig || typeof valuesLinkConfig !== 'object') {
      return null;
    }

    if (Object.prototype.hasOwnProperty.call(valuesLinkConfig, columnKey)) {
      return valuesLinkConfig[columnKey];
    }

    return null;
  }

  private renderLinkedCell(params: any, linkConfig: any): HTMLElement {
    const anchor = document.createElement('a');
    anchor.href = 'javascript:void(0)';
    anchor.textContent = String(params?.value ?? '');
    anchor.className = 'inbuilt-table-link';
    anchor.addEventListener('click', (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      this.openLinkInNewTab(params);
    });

    if (!params?.value) {
      anchor.classList.add('inbuilt-table-link--empty');
    }

    return anchor;
  }

  private buildLinkedUrl(linkConfig: any, rowData: any, columnKey: string, cellValue: any): string {
    const rawLink = typeof linkConfig === 'string'
      ? linkConfig
      : String(linkConfig?.link ?? '').trim();

    if (!rawLink) {
      return '';
    }

    const resolvedBaseUrl = this.getBaseUrl();
    const resolvedPath = rawLink.replace(/\{base_url\}/gi, resolvedBaseUrl).trim();
    const queryParams = this.buildLinkQueryParams(linkConfig, rowData, columnKey, cellValue);

    if (!queryParams) {
      return resolvedPath;
    }

    const separator = resolvedPath.includes('?') ? '&' : '?';
    return `${resolvedPath}${separator}${queryParams}`;
  }

  private buildLinkQueryParams(linkConfig: any, rowData: any, columnKey: string, cellValue: any): string {
    const paramsConfig = typeof linkConfig === 'object' && linkConfig && !Array.isArray(linkConfig)
      ? (linkConfig.params || {})
      : {};

    const queryParts: string[] = [];
    const appendParam = (paramName: string, value: any) => {
      const cleanParamName = String(paramName ?? '').trim();
      if (!cleanParamName || value === null || value === undefined || value === '') {
        return;
      }

      queryParts.push(`${encodeURIComponent(cleanParamName)}=${encodeURIComponent(String(value))}`);
    };

    Object.keys(paramsConfig || {}).forEach((sourceKey: string) => {
      const targetParamName = paramsConfig[sourceKey];
      const rowValue = Object.prototype.hasOwnProperty.call(rowData || {}, sourceKey)
        ? rowData[sourceKey]
        : (sourceKey === columnKey ? cellValue : undefined);

      appendParam(targetParamName, rowValue);
    });

    return queryParts.join('&');
  }

  private getBaseUrl(): string {
    const fallbackBaseUrl = window?.location?.origin || '';
    return String(fallbackBaseUrl).replace(/\/$/, '');
  }
}
